import React, { useState } from "react";
import { observer } from "mobx-react";
import copilotStore from "Stores/copilotStore";

/**
 * Structured requirement-gathering card (the agent's ask_user tool).
 * One question per page. SINGLE-select questions answer instantly on click
 * (no Submit) and advance; MULTI-select ones let you tick several options then
 * submit. No Skip/Previous — every question needs a real answer, since a
 * skipped answer still gets drafted from as if it were a real one. Answers
 * go back as one QUESTION_FORM_RESPONSE so the agent drafts from real
 * requirements instead of inventing generic copy.
 */
const QuestionFormBlock = observer(({ block }) => {
  const [pageIndex, setPageIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [chosen, setChosen] = useState([]); // selected option indices (multi)
  const [otherText, setOtherText] = useState("");

  const questions = block.questions || [];

  if (block.done) {
    const summary = (block.answers || [])
      .map(answer => answer.answer)
      .filter(Boolean)
      .join(" · ");
    return (
      <div className="copilotQForm copilotQForm--done">
        <i className="material-icons-outlined">check_circle</i>
        <span>
          {block.skipped
            ? "Answered in chat instead"
            : summary || "Skipped — using defaults"}
        </span>
      </div>
    );
  }

  const question = questions[pageIndex];
  if (!question) return null;
  const isLast = pageIndex === questions.length - 1;
  const multi = !!question.multiSelect;

  const resetPageState = () => {
    setChosen([]);
    setOtherText("");
  };

  // Record this question's answer and move on (or submit if last). `interpret`
  // marks the text as NOT a literal answer but a different request/intent the
  // user explicitly flagged (via the interpret icon) — forces immediate
  // submission even mid-form, since the point is to have the agent react to
  // it right away rather than finish collecting the remaining answers first.
  // Unanswered remaining questions are padded as skipped so the payload
  // shape stays consistent with a normal full submission.
  const advance = (answerLabel, interpret = false) => {
    const nextAnswers = [
      ...answers,
      { question: question.question, answer: answerLabel, ...(interpret ? { interpret: true } : {}) }
    ];
    if (isLast || interpret) {
      const remaining =
        interpret && !isLast
          ? questions.slice(pageIndex + 1).map(q => ({ question: q.question, answer: null }))
          : [];
      copilotStore.submitQuestionForm(block.id, [...nextAnswers, ...remaining]);
    } else {
      setAnswers(nextAnswers);
      setPageIndex(pageIndex + 1);
      resetPageState();
    }
  };

  const interpretOther = () => {
    if (!otherText.trim()) return;
    advance(otherText.trim(), true);
  };

  // Single-select: one click is the whole answer.
  const pickSingle = index => advance(question.options[index].label);

  // Multi-select: toggle membership; commit happens on Submit.
  const toggle = index => {
    setChosen(
      chosen.includes(index)
        ? chosen.filter(i => i !== index)
        : [...chosen, index]
    );
  };

  const submitMulti = () => {
    const labels = chosen.map(i => question.options[i].label);
    if (otherText.trim()) labels.push(otherText.trim());
    advance(labels.length ? labels.join(", ") : null);
  };

  const handlePrimary = () => {
    if (multi) submitMulti();
    else if (otherText.trim()) advance(otherText.trim());
  };

  const primaryEnabled = multi
    ? chosen.length > 0 || otherText.trim().length > 0
    : otherText.trim().length > 0;

  return (
    <div className="copilotQForm">
      <div className="copilotQForm__head">
        {question.header && (
          <span className="copilotQForm__chip">{question.header}</span>
        )}
        <span className="copilotQForm__question">{question.question}</span>
        {multi && <span className="copilotQForm__multiHint">Pick any</span>}
        {questions.length > 1 && (
          <span className="copilotQForm__count">
            {pageIndex + 1}/{questions.length}
          </span>
        )}
      </div>
      <div className="copilotQForm__options">
        {question.options.map((option, index) => {
          const isChosen = multi && chosen.includes(index);
          return (
            <button
              type="button"
              key={option.label}
              className={`copilotQForm__option ${isChosen ? "selected" : ""} ${
                multi ? "copilotQForm__option--multi" : ""
              }`}
              onClick={() => (multi ? toggle(index) : pickSingle(index))}
            >
              {multi && (
                <i className="material-icons-outlined copilotQForm__check">
                  {isChosen ? "check_box" : "check_box_outline_blank"}
                </i>
              )}
              <span className="copilotQForm__optionText">
                <span className="copilotQForm__optionLabel">
                  {option.label}
                  {option.recommended && (
                    <span className="copilotQForm__optionRecommended">
                      {" "}(Recommended)
                    </span>
                  )}
                </span>
                {option.description && (
                  <span className="copilotQForm__optionDesc">
                    {option.description}
                  </span>
                )}
              </span>
            </button>
          );
        })}
        <div
          className={`copilotQForm__other ${
            otherText.trim() ? "selected" : ""
          }`}
        >
          <input
            value={otherText}
            placeholder="Other, type your own"
            onChange={event => setOtherText(event.target.value)}
            onKeyDown={event => {
              if (event.key === "Enter" && otherText.trim()) {
                if (multi) submitMulti();
                else advance(otherText.trim());
              }
            }}
          />
          {!multi && otherText.trim() && (
            <i
              className="material-icons-outlined copilotQForm__interpretIcon"
              title="Not an answer — treat this as a request instead"
              onClick={interpretOther}
            >
              chat_bubble_outline
            </i>
          )}
        </div>
      </div>
      <div className="copilotQForm__footer">
        <div className="copilotQForm__footerRight">
          {/* Single-select answers on click, so the primary button is only for
              a typed "Other"; multi-select always needs it to submit ticks. */}
          {(multi || otherText.trim()) && (
            <button
              type="button"
              className="copilotQForm__primaryBtn"
              disabled={!primaryEnabled}
              onClick={handlePrimary}
            >
              {isLast ? "Submit" : "Next"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
});

export default QuestionFormBlock;
